import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAdminAuth } from "./AdminAuthContext";
import { ShieldCheck } from "lucide-react";

/**
 * Shown after a successful password login when the account has a verified
 * TOTP factor. The Supabase session is aal1 at this point; entering the
 * correct 6-digit code elevates it to aal2 and the middleware then allows
 * every /api/admin/* call.
 */
export default function AdminMFAChallenge() {
  const { session, signOut, startSession } = useAdminAuth();
  const navigate = useNavigate();
  const [factorId, setFactorId] = useState(null);
  const [challengeId, setChallengeId] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      setError("");
      const { data, error: err } = await supabase.auth.mfa.listFactors();
      if (err) { setError(err.message); return; }
      const factor = (data.totp || []).find((f) => f.status === "verified");
      if (!factor) {
        // No factor. Just move on.
        navigate("/admin", { replace: true });
        return;
      }
      const { data: ch, error: cErr } = await supabase.auth.mfa.challenge({ factorId: factor.id });
      if (cErr) { setError(cErr.message); return; }
      setFactorId(factor.id);
      setChallengeId(ch.id);
      setReady(true);
    })();
  }, [navigate]);

  const verify = async () => {
    if (!factorId || !challengeId || code.length !== 6) return;
    setBusy(true); setError("");
    try {
      const { error: err } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code: code.replace(/\s/g, ""),
      });
      if (err) throw err;
      const { data: refreshed } = await supabase.auth.refreshSession();
      // Now aal2 — mint the server-side session (rejected while aal1).
      const token = refreshed?.session?.access_token;
      if (token) await startSession(token);
      navigate("/admin", { replace: true });
    } catch (e) {
      setError(e.message || "Wrong code. Try again.");
      setCode("");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    await signOut();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div style={pageStyle} data-testid="admin-mfa-challenge">
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <ShieldCheck size={22} style={{ color: "#C42021" }} />
          <h1 style={titleStyle}>Two-factor check</h1>
        </div>
        <p style={mutedStyle}>
          Enter the 6-digit code from Google Authenticator to finish signing in as{" "}
          <span style={{ color: "#ECECEE", fontFamily: "'JetBrains Mono', monospace" }}>
            {session?.user?.email || "admin"}
          </span>.
        </p>

        <input
          className="adm-input"
          inputMode="numeric"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter" && code.length === 6) verify(); }}
          autoFocus
          disabled={!ready || busy}
          data-testid="mfa-challenge-code"
          style={{
            marginTop: 18,
            marginBottom: 14,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: "0.3em",
            fontSize: 24,
            textAlign: "center",
            padding: "14px 12px",
          }}
        />
        <button
          className="adm-btn"
          onClick={verify}
          disabled={busy || code.length !== 6 || !ready}
          data-testid="mfa-challenge-verify"
          style={{ width: "100%", justifyContent: "center", padding: "12px" }}
        >
          {busy ? "Verifying..." : "Continue"}
        </button>
        <button
          className="adm-btn adm-btn-ghost"
          onClick={cancel}
          style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
        >
          Cancel and sign out
        </button>

        {error && <p style={{ color: "#EF4444", marginTop: 12, fontSize: 13 }} data-testid="mfa-challenge-error">{error}</p>}
        <p style={{ ...mutedStyle, marginTop: 18 }}>
          Lost your authenticator? Contact the store owner from another admin device to reset it.
        </p>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  background: "#0A0A0B",
  color: "#ECECEE",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
};
const cardStyle = {
  width: "100%",
  maxWidth: 420,
  background: "#17171A",
  border: "1px solid #26262C",
  borderRadius: 14,
  padding: 26,
};
const titleStyle = {
  margin: 0,
  fontFamily: "'Anton', 'Arial Black', Impact, sans-serif",
  fontSize: 28,
  letterSpacing: "0.02em",
  textTransform: "uppercase",
};
const mutedStyle = {
  color: "#8B8B93",
  fontSize: 13,
  lineHeight: 1.55,
};
